import { useEffect, useState, useCallback } from 'react';
import useSettings from '../hooks/useSettings';
import useContent from '../hooks/useContent';

export default function NavigationEdit() {
  const settingsApi = useSettings();
  const { listContent } = useContent();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [siteTitle, setSiteTitle] = useState('Headless CMS');
  const [siteDescription, setSiteDescription] = useState('');
  const [navLinks, setNavLinks] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  const [availablePosts, setAvailablePosts] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [settingsData, pagesRes, postsRes] = await Promise.all([
        settingsApi.getSettings(),
        listContent({ type: 'page', limit: 100 }),
        listContent({ type: 'post', limit: 100 }),
      ]);

      setSiteTitle(settingsData.siteTitle || 'Headless CMS');
      setSiteDescription(settingsData.siteDescription || '');
      setNavLinks(
        (settingsData.navLinks || []).map((item, index) => ({
          id: item.id || `nav-${Date.now()}-${index}`,
          label: item.label || '',
          href: item.href || '',
          target: item.target || '_self',
          type: item.type || (item.href?.startsWith('/posts/') ? 'post' : item.href?.startsWith('/') ? 'page' : 'custom'),
          pageId: item.pageId || '',
        })),
      );
      setAvailablePages(pagesRes.items || []);
      setAvailablePosts(postsRes.items || []);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Navigationsdaten.');
    } finally {
      setLoading(false);
    }
  }, [settingsApi, listContent]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const addMenuItem = () => {
    setNavLinks((prev) => [
      ...prev,
      {
        id: `nav-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        label: 'Neuer Link',
        href: '/',
        target: '_self',
        type: 'custom',
        pageId: '',
      },
    ]);
  };

  const updateMenuItem = (index, field, value) => {
    setNavLinks((prev) => {
      const updated = [...prev];
      const current = { ...updated[index], [field]: value };

      if (field === 'type') {
        if (value === 'page' && availablePages.length > 0) {
          const firstPage = availablePages[0];
          current.pageId = firstPage.contentId;
          current.href = `/${firstPage.slug}/`;
          if (!current.label || current.label === 'Neuer Link') current.label = firstPage.title;
        } else if (value === 'post' && availablePosts.length > 0) {
          const firstPost = availablePosts[0];
          current.pageId = firstPost.contentId;
          current.href = `/posts/${firstPost.slug}/`;
          if (!current.label || current.label === 'Neuer Link') current.label = firstPost.title;
        } else if (value === 'custom') {
          current.pageId = '';
          current.href = '/';
        }
      } else if (field === 'pageId') {
        if (current.type === 'page') {
          const matched = availablePages.find((p) => p.contentId === value);
          if (matched) {
            current.href = `/${matched.slug}/`;
            current.label = matched.title;
          }
        } else if (current.type === 'post') {
          const matched = availablePosts.find((p) => p.contentId === value);
          if (matched) {
            current.href = `/posts/${matched.slug}/`;
            current.label = matched.title;
          }
        }
      }

      updated[index] = current;
      return updated;
    });
  };

  const moveMenuItem = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= navLinks.length) return;
    setNavLinks((prev) => {
      const copy = [...prev];
      [copy[index], copy[target]] = [copy[target], copy[index]];
      return copy;
    });
  };

  const removeMenuItem = (index) => {
    setNavLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const cleanNavLinks = navLinks.map((item) => ({
        id: item.id,
        label: item.label.trim() || 'Link',
        href: item.href.trim() || '/',
        target: item.target || '_self',
        type: item.type || 'custom',
        pageId: item.pageId || '',
      }));

      const updatedSettings = await settingsApi.updateSettings({
        siteTitle,
        siteDescription,
        navLinks: cleanNavLinks,
      });

      await new Promise((r) => setTimeout(r, 1000));
      const freshSettings = await settingsApi.getSettings(true);
      if (freshSettings && Array.isArray(freshSettings.navLinks)) {
        setNavLinks(freshSettings.navLinks);
      }
      setMessage('Navigationsmenü erfolgreich gespeichert. Die Änderungen werden beim nächsten Veröffentlichen übernommen.');
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern des Navigationsmenüs.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stack gap-lg">
      <section className="card">
        <div className="row between responsive-gap">
          <div>
            <h2>Menü & Navigation (AEM EDS Block)</h2>
            <p className="muted">
              Erstelle und verwalte die Navigationsleiste der publizierten Website. Ausgewählte Zielseiten werden automatisch verknüpft.
            </p>
          </div>
          <button className="button primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Speichert… (1s Sperre)' : 'Menü speichern'}
          </button>
        </div>

        {message ? <div className="alert success" style={{ marginTop: '1rem' }}>{message}</div> : null}
        {error ? <div className="alert error" style={{ marginTop: '1rem' }}>{error}</div> : null}
      </section>

      {/* Live AEM EDS Navigation Block Header Preview */}
      <section className="card" style={{ background: '#0f172a', border: '1px solid #1e293b', color: '#fff' }}>
        <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#94a3b8', fontWeight: 600, marginBottom: '0.75rem' }}>
          Vorschau: AEM EDS Navigation Block (nav.html)
        </div>
        <header style={{ background: 'transparent', padding: '0.5rem 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ fontWeight: 800, fontSize: '1.25rem', color: '#fff' }}>
              <a href="/" style={{ color: '#fff', textDecoration: 'none' }}>{siteTitle}</a>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {navLinks.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  target={item.target}
                  style={{
                    color: '#bfdbfe',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    padding: '0.3rem 0.6rem',
                    borderRadius: '6px',
                    background: 'rgba(255,255,255,0.05)',
                  }}
                >
                  {item.label || 'Unbenannt'}
                </a>
              ))}
            </div>
          </nav>
        </header>
      </section>

      {/* Navigation Links Editor */}
      <section className="card stack gap-md" style={{ opacity: saving ? 0.7 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
        <div className="row between">
          <h3>Menüeinträge ({navLinks.length})</h3>
          <button type="button" className="button secondary small" onClick={addMenuItem} disabled={saving}>
            + Menüeintrag hinzufügen
          </button>
        </div>

        {loading ? (
          <p className="muted">Lade Navigation…</p>
        ) : navLinks.length === 0 ? (
          <div className="media-empty">
            <p className="muted">Keine Menüeinträge vorhanden. Klicke auf „+ Menüeintrag hinzufügen“.</p>
          </div>
        ) : (
          <div className="stack gap-sm">
            {navLinks.map((item, index) => (
              <div
                key={item.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr 1fr 1fr auto auto',
                  gap: '0.75rem',
                  alignItems: 'center',
                  padding: '1rem',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                }}
              >
                {/* Index badge */}
                <span className="badge" style={{ background: '#e2e8f0', color: '#475569' }}>
                  #{index + 1}
                </span>

                {/* Label input */}
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '0.2rem' }}>
                    Beschriftung
                  </label>
                  <input
                    type="text"
                    value={item.label}
                    onChange={(e) => updateMenuItem(index, 'label', e.target.value)}
                    placeholder="z.B. Über uns"
                    style={{ width: '100%' }}
                    disabled={saving}
                  />
                </div>

                {/* Link type selector */}
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '0.2rem' }}>
                    Ziel-Typ
                  </label>
                  <select
                    value={item.type}
                    onChange={(e) => updateMenuItem(index, 'type', e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="page">Seite auswählen</option>
                    <option value="post">Blog-Beitrag auswählen</option>
                    <option value="custom">Benutzerdefinierte URL</option>
                  </select>
                </div>

                {/* Target Picker / URL Input */}
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '0.2rem' }}>
                    {item.type === 'page' ? 'Statische Seite' : item.type === 'post' ? 'Blog-Beitrag' : 'Ziel-URL'}
                  </label>
                  {item.type === 'page' ? (
                    <select
                      value={item.pageId}
                      onChange={(e) => updateMenuItem(index, 'pageId', e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="">-- Seite wählen --</option>
                      {availablePages.map((p) => (
                        <option key={p.contentId} value={p.contentId}>
                          {p.title} (/{p.slug})
                        </option>
                      ))}
                    </select>
                  ) : item.type === 'post' ? (
                    <select
                      value={item.pageId}
                      onChange={(e) => updateMenuItem(index, 'pageId', e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="">-- Beitrag wählen --</option>
                      {availablePosts.map((p) => (
                        <option key={p.contentId} value={p.contentId}>
                          {p.title} (/posts/{p.slug})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={item.href}
                      onChange={(e) => updateMenuItem(index, 'href', e.target.value)}
                      placeholder="https://... oder /ziel"
                      style={{ width: '100%' }}
                    />
                  )}
                </div>

                {/* Target window checkbox */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={item.target === '_blank'}
                    onChange={(e) => updateMenuItem(index, 'target', e.target.checked ? '_blank' : '_self')}
                  />
                  Neuer Tab
                </label>

                {/* Actions */}
                <div className="button-row compact">
                  <button
                    type="button"
                    className="button ghost small"
                    onClick={() => moveMenuItem(index, -1)}
                    disabled={index === 0}
                    title="Nach oben"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="button ghost small"
                    onClick={() => moveMenuItem(index, 1)}
                    disabled={index === navLinks.length - 1}
                    title="Nach unten"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="button danger small"
                    onClick={() => removeMenuItem(index)}
                    title="Entfernen"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
